import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Bot, User, MessageCircle, Plus, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Messages = () => {
  const { user } = useAuth();
  const { theme, themes } = useTheme();
  const currentTheme = themes[theme] || themes.electric;
  const [searchParams] = useSearchParams();

  const [conversations, setConversations] = useState([]);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [showMobileChat, setShowMobileChat] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    // Check if we need to open a conversation with a property owner
    const propertyId = searchParams.get('property');
    const ownerId = searchParams.get('owner');
    
    if (propertyId && ownerId && conversations.length > 0) {
      // Find or create conversation with property owner
      const existingConvo = conversations.find(c => 
        c.type === 'user' && c.other_user_id === ownerId
      );
      
      if (existingConvo) {
        selectConversation(existingConvo);
      } else {
        // Create new conversation about this property
        createPropertyConversation(propertyId, ownerId);
      }
    }
  }, [searchParams, conversations]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadConversations = async () => {
    try {
      const resp = await axios.get(`${API}/messages/conversations`);
      let convos = resp.data;
      
      // If no support bot conversation exists, create one client-side
      const hasSupportBot = convos.some(c => c.type === 'support_bot');
      if (!hasSupportBot) {
        // Initialize with a welcome message by sending a first-contact request
        const supportConvo = {
          conversation_id: `conv_support_${user?.user_id || 'new'}`,
          user_id: user?.user_id,
          type: 'support_bot',
          name: 'Asistente E-Home',
          avatar: null,
          last_message: 'Hola! Soy tu asistente virtual. Como puedo ayudarte?',
          updated_at: new Date().toISOString()
        };
        convos = [supportConvo, ...convos];
      }
      
      setConversations(convos);
      if (convos.length > 0 && !selectedConvo) {
        selectConversation(convos[0]);
      }
    } catch (error) {
      // Create default support conversation if API fails
      const defaultConvo = {
        conversation_id: 'conv_support_default',
        type: 'support_bot',
        name: 'Asistente E-Home',
        last_message: 'Hola! Soy tu asistente virtual.',
        updated_at: new Date().toISOString()
      };
      setConversations([defaultConvo]);
      selectConversation(defaultConvo);
    }
  };

  const createPropertyConversation = async (propertyId, ownerId) => {
    try {
      // Get property info
      const propertyResp = await axios.get(`${API}/properties/${propertyId}`);
      const property = propertyResp.data;
      
      const initialMessage = `Hola, estoy interesado en tu propiedad "${property.title}". ¿Podrías darme más información?`;
      
      // Create conversation via backend API
      const resp = await axios.post(`${API}/messages/conversations/create`, {
        other_user_id: ownerId,
        property_id: propertyId,
        initial_message: initialMessage
      });
      
      const newConvo = resp.data;
      
      // Reload conversations to get the new one
      await loadConversations();
      
      // Select the new conversation
      setSelectedConvo(newConvo);
      setShowMobileChat(true);
      
      toast.success('Conversación iniciada con el propietario');
    } catch (error) {
      console.error('Error creating property conversation:', error);
      toast.error('No se pudo iniciar la conversación. Por favor, intenta de nuevo.');
    }
  };

  const selectConversation = async (convo) => {
    setSelectedConvo(convo);
    setShowMobileChat(true);
    try {
      const resp = await axios.get(`${API}/messages/${convo.conversation_id}`);
      setMessages(resp.data);
      
      // Mark messages as read
      await axios.post(`${API}/messages/${convo.conversation_id}/mark-read`, {});
    } catch (error) {
      // Show default welcome message if no messages yet
      setMessages([{
        message_id: 'welcome',
        sender: 'bot',
        sender_id: 'bot',
        sender_name: 'Asistente E-Home',
        sender_avatar: null,
        text: 'Hola! Soy el asistente virtual de E-Home. Puedo ayudarte con:\n\n- Buscar propiedades\n- Publicar anuncios\n- Informacion sobre suscripciones\n- Dudas sobre la plataforma\n\nEscribe tu pregunta!',
        timestamp: new Date().toISOString()
      }]);
    }
  };

  const handleSend = async () => {
    if (!inputText.trim() || sending || !selectedConvo) return;
    
    const text = inputText;
    setInputText('');
    setSending(true);

    // Optimistic UI update
    const tempUserMsg = {
      message_id: `temp_${Date.now()}`,
      sender: 'user',
      sender_id: user?.user_id,
      sender_name: user?.name || 'Tu',
      sender_avatar: user?.picture,
      text: text,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      // First ensure conversation exists in DB
      const resp = await axios.post(`${API}/messages/send`, {
        conversation_id: selectedConvo.conversation_id,
        text: text
      });

      if (resp.data.bot_response) {
        setMessages(prev => [...prev, resp.data.bot_response]);
      }
      
      // Refresh conversations to update last_message
      const convResp = await axios.get(`${API}/messages/conversations`);
      if (convResp.data.length > 0) {
        setConversations(convResp.data);
      }
    } catch (error) {
      // If conversation doesn't exist yet, we need to create it
      if (error.response?.status === 404) {
        // Create the conversation first
        try {
          // We'll handle this by adding a mock bot response
          const botMsg = {
            message_id: `bot_${Date.now()}`,
            sender: 'bot',
            sender_id: 'bot',
            sender_name: 'Asistente E-Home',
            sender_avatar: null,
            text: 'Bienvenido al chat de asistencia de E-Home! Estoy aqui para ayudarte con cualquier pregunta sobre el portal inmobiliario. Que necesitas?',
            timestamp: new Date().toISOString()
          };
          setMessages(prev => [...prev, botMsg]);
        } catch (innerError) {
          console.error('Error creating conversation:', innerError);
        }
      } else {
        toast.error('Error al enviar el mensaje');
      }
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const d = new Date(timestamp);
      return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  return (
    <div className="h-screen bg-gray-950 flex">
      {/* Conversations List */}
      <div className={`${showMobileChat ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 lg:w-96 border-r border-white/10`}>
        <div className="p-4 pt-16 border-b border-white/10">
          <h1 className={`text-2xl font-bold bg-gradient-to-r ${currentTheme.gradient} bg-clip-text text-transparent`} data-testid="messages-title">
            Mensajeria
          </h1>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          {conversations.map(conv => (
            <button
              key={conv.conversation_id}
              onClick={() => selectConversation(conv)}
              className={`w-full p-4 text-left border-b border-white/5 transition-all ${
                selectedConvo?.conversation_id === conv.conversation_id 
                  ? 'bg-white/10' 
                  : 'hover:bg-white/5'
              }`}
              data-testid={`conversation-${conv.conversation_id}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${
                  conv.type === 'support_bot' ? currentTheme.gradient : 'from-gray-600 to-gray-700'
                } flex items-center justify-center flex-shrink-0`}>
                  {conv.type === 'support_bot' ? <Bot size={18} className="text-white" /> : <User size={18} className="text-white" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-center mb-0.5">
                    <span className="font-semibold text-sm text-white truncate">{conv.name || 'Asistente'}</span>
                    <span className="text-xs text-gray-500 flex-shrink-0">{formatTime(conv.updated_at)}</span>
                  </div>
                  <p className="text-xs text-gray-400 truncate">{conv.last_message}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
      
      {/* Chat Area */}
      <div className={`${showMobileChat ? 'flex' : 'hidden md:flex'} flex-col flex-1`}>
        {selectedConvo ? (
          <>
            {/* Chat Header */}
            <div className="p-4 pt-16 md:pt-4 border-b border-white/10 flex items-center gap-3">
              <button 
                onClick={() => setShowMobileChat(false)}
                className="md:hidden p-2 rounded-lg hover:bg-white/10 text-gray-400"
              >
                <ArrowLeft size={20} />
              </button>
              <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${
                selectedConvo.type === 'support_bot' ? currentTheme.gradient : 'from-gray-600 to-gray-700'
              } flex items-center justify-center flex-shrink-0`}>
                {selectedConvo.type === 'support_bot' ? <Bot size={18} className="text-white" /> : <User size={18} className="text-white" />}
              </div>
              <div>
                <h2 className="font-semibold text-white text-sm">{selectedConvo.name || 'Asistente'}</h2>
                <p className="text-xs text-green-400">En linea</p>
              </div>
            </div>
            
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
              {messages.map((msg, idx) => {
                const isMyMessage = msg.sender_id === user?.user_id;
                return (
                  <div
                    key={msg.message_id || idx}
                    className={`flex items-end gap-2 ${isMyMessage ? 'justify-end' : 'justify-start'}`}
                  >
                    {/* Avatar for received messages (left side) */}
                    {!isMyMessage && (
                      <div className="flex-shrink-0 w-8 h-8 rounded-full overflow-hidden bg-gray-700">
                        {msg.sender_avatar ? (
                          <img src={msg.sender_avatar} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className={`w-full h-full flex items-center justify-center ${
                            msg.sender === 'bot' ? `bg-gradient-to-br ${currentTheme.gradient}` : 'bg-gray-600'
                          } text-white text-xs font-bold`}>
                            {msg.sender === 'bot' ? '🤖' : (msg.sender_name?.[0]?.toUpperCase() || 'U')}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Message bubble */}
                    <div className={`max-w-[75%] md:max-w-[65%] ${
                      isMyMessage
                        ? `bg-gradient-to-r ${currentTheme.gradient} text-white rounded-tl-2xl rounded-tr-2xl rounded-bl-2xl rounded-br-sm`
                        : 'bg-gray-900 text-white rounded-tl-sm rounded-tr-2xl rounded-bl-2xl rounded-br-2xl'
                    } px-4 py-3 shadow-lg`}>
                      <p className="text-sm whitespace-pre-wrap break-words">{msg.text}</p>
                      <span className="text-[10px] opacity-60 mt-1 block text-right">{formatTime(msg.timestamp)}</span>
                    </div>

                    {/* Avatar for sent messages (right side) - optional */}
                    {isMyMessage && (
                      <div className="flex-shrink-0 w-8 h-8 rounded-full overflow-hidden bg-gray-700">
                        {user?.picture ? (
                          <img src={user.picture} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${currentTheme.gradient} text-white text-xs font-bold`}>
                            {user?.name?.[0]?.toUpperCase() || 'U'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-gray-800 rounded-2xl px-4 py-3">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{animationDelay: '0ms'}} />
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{animationDelay: '150ms'}} />
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{animationDelay: '300ms'}} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            
            {/* Input */}
            <div className="p-4 border-t border-white/10">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                  placeholder="Escribe un mensaje..."
                  className="flex-1 bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500/40"
                  data-testid="message-input"
                />
                <button 
                  onClick={handleSend}
                  disabled={sending || !inputText.trim()}
                  className={`p-3 rounded-xl bg-gradient-to-r ${currentTheme.gradient} text-white hover:opacity-90 disabled:opacity-50 transition-all`}
                  data-testid="send-message-btn"
                >
                  <Send size={18} />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-gray-500">
              <MessageCircle size={48} className="mx-auto mb-4 opacity-30" />
              <p className="text-sm">Selecciona una conversacion</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
